import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Container, Row, Col, Button, Alert, Card, Form, Collapse } from 'react-bootstrap';
import authService from '../services/authService';
import BackToDashboard from '../components/BackToDashboard';

const ALL_ARCHETYPES = [
  { name: 'Hero', emoji: '🦸', description: 'Brave, determined, inspiring' },
  { name: 'Outlaw', emoji: '🏴‍☠️', description: 'Rebellious, disruptive, liberating' },
  { name: 'Magician', emoji: '🪄', description: 'Transformative, visionary, charismatic' },
  { name: 'Sage', emoji: '🦉', description: 'Wise, knowledgeable, trusted advisor' },
  { name: 'Explorer', emoji: '🧭', description: 'Adventurous, independent, pioneering' },
  { name: 'Creator', emoji: '🎨', description: 'Innovative, artistic, expressive' },
  { name: 'Ruler', emoji: '👑', description: 'Authoritative, responsible, leader' },
  { name: 'Caregiver', emoji: '🤗', description: 'Nurturing, supportive, generous' },
  { name: 'Innocent', emoji: '🌸', description: 'Optimistic, pure, trustworthy' },
  { name: 'Jester', emoji: '🃏', description: 'Fun, playful, entertaining' },
  { name: 'Lover', emoji: '💕', description: 'Passionate, intimate, committed' },
  { name: 'Everyman', emoji: '🤝', description: 'Relatable, authentic, down-to-earth' },
];

function BrandQuizIntro() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [prereqError, setPrereqError] = useState('');
  const [saveError, setSaveError] = useState('');
  const [businessName, setBusinessName] = useState('');
  const [nicheName, setNicheName] = useState('');
  const [showManualEntry, setShowManualEntry] = useState(false);
  const [archetypeValues, setArchetypeValues] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    checkAuthAndLoad();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const checkAuthAndLoad = async () => {
    // Check authentication
    const authResult = await authService.checkAuth();
    if (!authResult.authenticated) {
      navigate('/login');
      return;
    }

    // Check prerequisites
    try {
      const prereqResponse = await fetch('/api/brand/check-prerequisites', {
        credentials: 'include'
      });

      if (prereqResponse.ok) {
        const prereqData = await prereqResponse.json();
        if (!prereqData.prerequisites_met) {
          setPrereqError(prereqData.missing);
          setLoading(false);
          return;
        }
      } else {
        setPrereqError('Failed to check prerequisites');
        setLoading(false);
        return;
      }

      // Check if archetypes already exist — if so, go straight to results
      const archetypeResponse = await fetch('/api/brand/get-archetypes', {
        credentials: 'include'
      });

      if (archetypeResponse.ok) {
        const archetypeData = await archetypeResponse.json();
        if (archetypeData.primary) {
          navigate('/dashboard/brand-results');
          return;
        }
      }

      // Load business context
      const contextResponse = await fetch('/api/brand/get-context', {
        credentials: 'include'
      });

      if (contextResponse.ok) {
        const contextData = await contextResponse.json();
        setBusinessName(contextData.business_name);
        setNicheName(contextData.niche_name);
      } else {
        setPrereqError('Failed to load business context');
      }

    } catch (err) {
      setPrereqError('Error loading data: ' + err.message);
    }

    setLoading(false);
  };

  const startQuiz = () => {
    navigate('/dashboard/brand-quiz');
  };

  const handleArchetypeChange = (name, value) => {
    const numValue = parseInt(value) || 0;
    setArchetypeValues(prev => ({
      ...prev,
      [name]: Math.min(100, Math.max(0, numValue))
    }));
  };

  const getTotalPercentage = () => {
    return Object.values(archetypeValues).reduce((sum, val) => sum + (val || 0), 0);
  };

  const saveManualArchetypes = async () => {
    const total = getTotalPercentage();
    if (total === 0) {
      setSaveError('Please enter at least one archetype percentage');
      return;
    }

    setSaving(true);
    setSaveError('');

    try {
      const response = await fetch('/api/brand/set-archetypes-manual', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ archetypes: archetypeValues })
      });

      if (response.ok) {
        // Navigate to brand results or next step
        navigate('/dashboard/brand-results');
      } else {
        const data = await response.json();
        setSaveError(data.error || 'Failed to save archetypes');
      }
    } catch (err) {
      setSaveError('Error saving archetypes: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Container className="py-5 text-center">
        <p>Loading...</p>
      </Container>
    );
  }

  if (prereqError) {
    return (
      <Container className="py-5">
        <Row className="justify-content-center">
          <Col md={8}>
            <Alert variant="danger">
              {prereqError}
            </Alert>
            <Button variant="outline-secondary" onClick={() => navigate('/dashboard/branding')}>
              Back to Dashboard
            </Button>
          </Col>
        </Row>
      </Container>
    );
  }

  return (
    <Container className="py-5">
      <BackToDashboard module="branding" />
      <Row className="justify-content-center">
        <Col md={10} lg={8}>
          <Card className="shadow">
            <Card.Body className="p-5">
              <div className="text-center mb-4">
                <h1 className="display-5 mb-3">🎨 BUILD YOUR BRAND IDENTITY</h1>
                <hr className="my-4" />
              </div>

              {businessName && nicheName && (
                <Alert variant="primary" className="mb-4">
                  <p className="mb-1"><strong>Your business:</strong> {businessName}</p>
                  <p className="mb-0"><strong>Your market:</strong> {nicheName}</p>
                </Alert>
              )}

              <p className="lead mb-4">
                You've defined <strong>WHAT</strong> you offer and <strong>WHO</strong> you serve.
              </p>

              <p className="lead mb-4">
                Now let's define <strong>HOW</strong> you look and feel.
              </p>

              <hr className="my-4" />

              <h4 className="mb-3">Brand Archetype Assessment</h4>

              <p className="mb-4">
                Every successful brand embodies one of 12 universal archetypes—psychological patterns that resonate deeply with customers.
              </p>

              <p className="mb-4">
                We'll ask you <strong>6 questions</strong> to discover your brand archetype, then generate a complete brand identity:
              </p>

              <ul className="mb-4" style={{ lineHeight: '1.8' }}>
                <li>Color palette (7 colors)</li>
                <li>Typography recommendations</li>
                <li>Logo shapes and direction</li>
                <li>Iconography style</li>
                <li>Textures and patterns</li>
                <li>Visual mood board</li>
              </ul>

              <p className="mb-4">
                This takes <strong>10-15 minutes</strong> and ensures all your marketing materials have a cohesive, professional look.
              </p>

              <hr className="my-4" />

              <h5 className="mb-3">How to Answer:</h5>

              <p className="mb-3">You can answer naturally:</p>

              <ul className="mb-4">
                <li><strong>Single choice:</strong> "A"</li>
                <li><strong>Combined:</strong> "A with a little C"</li>
                <li><strong>Weighted:</strong> "Mostly B with some E and a touch of D"</li>
              </ul>

              <Alert variant="info">
                <strong>📝 There are no wrong answers</strong>—just be authentic to your brand.
              </Alert>

              <hr className="my-4" />

              <div className="text-center mb-3">
                <Button
                  variant="link"
                  onClick={() => setShowManualEntry(!showManualEntry)}
                  className="text-muted"
                >
                  {showManualEntry ? '↑ Hide manual entry' : '↓ I already know my archetype'}
                </Button>
              </div>

              <Collapse in={showManualEntry}>
                <div>
                  <Card className="bg-light mb-4">
                    <Card.Body>
                      <h5 className="mb-3">Enter Your Archetype Mix</h5>
                      <p className="text-muted small mb-3">
                        Enter percentages for each archetype that represents your brand.
                        They don't need to add up to 100% - we'll normalize them.
                      </p>

                      <Row>
                        {ALL_ARCHETYPES.map((arch) => (
                          <Col xs={6} md={4} key={arch.name} className="mb-3">
                            <Form.Group>
                              <Form.Label className="small mb-1">
                                {arch.emoji} {arch.name}
                              </Form.Label>
                              <div className="d-flex align-items-center">
                                <Form.Control
                                  type="number"
                                  min="0"
                                  max="100"
                                  value={archetypeValues[arch.name] || ''}
                                  onChange={(e) => handleArchetypeChange(arch.name, e.target.value)}
                                  placeholder="0"
                                  size="sm"
                                  style={{ width: '70px' }}
                                />
                                <span className="ms-1 text-muted small">%</span>
                              </div>
                              <Form.Text className="text-muted" style={{ fontSize: '0.7rem' }}>
                                {arch.description}
                              </Form.Text>
                            </Form.Group>
                          </Col>
                        ))}
                      </Row>

                      {saveError && (
                        <Alert variant="danger" className="mt-3" dismissible onClose={() => setSaveError('')}>
                          {saveError}
                        </Alert>
                      )}

                      <div className="d-flex justify-content-between align-items-center mt-3 pt-3 border-top">
                        <div>
                          <strong>Total: {getTotalPercentage()}%</strong>
                          {getTotalPercentage() > 0 && (
                            <span className="text-muted ms-2 small">
                              (will be normalized to 100%)
                            </span>
                          )}
                        </div>
                        <Button
                          variant="success"
                          onClick={saveManualArchetypes}
                          disabled={saving || getTotalPercentage() === 0}
                        >
                          {saving ? 'Saving...' : 'Save & Continue →'}
                        </Button>
                      </div>
                    </Card.Body>
                  </Card>
                </div>
              </Collapse>

              <div className="d-flex justify-content-between mt-5">
                <Button
                  variant="outline-secondary"
                  onClick={() => navigate('/dashboard/branding')}
                >
                  ← Back to Dashboard
                </Button>
                <Button
                  variant="primary"
                  size="lg"
                  onClick={startQuiz}
                >
                  Start Brand Assessment →
                </Button>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
}

export default BrandQuizIntro;
